import React from 'react';
import { SEO } from '../components/seo/SEO';
import { useLanguage } from '../context/LanguageContext';
import { ProductLandingPage } from '../components/content/ProductLandingPage';

export const LicenseDefensePage: React.FC = () => {
  const { language } = useLanguage();

  const content = {
    de: {
      seo: {
        title: 'Strategisches Software Asset Management – VMware Kostenstrategie',
        description: 'Planbare OpEx mit einer strukturierten Asset- und Lizenzanalyse für VMware-Umgebungen.',
      },
      hero: {
        eyebrow: 'Strategisches Software Asset Management',
        title: 'VMware Kosten- & Sourcing-Strategie',
        subtitle:
          'Schaffen Sie planbare OpEx und eine belastbare Verhandlungsbasis mit einer strukturierten Asset-Analyse.',
        cta: { label: 'Asset-Management-Audit anfragen', href: '/consultation' },
      },
      problem: {
        title: 'Strategischer Bedarf',
        items: [
          'Konsolidierung im Vendor-Markt erhöht Kostenvolatilität.',
          'Core-Minimums und Bundle-Logik verzerren reale Nutzung.',
          'Einkauf benötigt eine belastbare Datenbasis für Verhandlungen.',
          'Sourcing- und Migrationsentscheidungen brauchen Business-Impact-Szenarien.',
        ],
      },
      solution: {
        title: 'Unser Ansatz',
        items: [
          {
            title: 'Asset & Cost Baseline',
            desc: 'Inventar, Nutzung und Lizenzprofile als Verhandlungsgrundlage.',
          },
          {
            title: 'Sourcing Scenario Model',
            desc: 'Renewal-, Konsolidierungs- und Migrationsszenarien mit TCO.',
          },
          {
            title: 'Executive Summary',
            desc: 'Board- und Procurement-ready Empfehlungen inkl. Maßnahmenplan.',
          },
        ],
      },
      steps: {
        title: 'Engagement-Modell',
        items: [
          { title: 'Assess', desc: 'RVTools/CMDB-Daten erfassen – ohne Agents.' },
          { title: 'Model', desc: 'Kosten- und Szenarienmodell auf reale Nutzung anwenden.' },
          { title: 'Decide', desc: 'Strategie- und Umsetzungsplan mit Prioritäten.' },
        ],
      },
      cta: {
        title: 'Bereit für eine belastbare Asset-Strategie?',
        subtitle: 'Wir liefern in wenigen Wochen die Entscheidungsgrundlage für OpEx, Sourcing und Migration.',
        button: { label: 'Asset-Management-Audit anfragen', href: '/consultation' },
      },
    },
    en: {
      seo: {
        title: 'Strategic Software Asset Management – VMware Cost Strategy',
        description: 'Create predictable OpEx with a structured asset and licensing review for VMware environments.',
      },
      hero: {
        eyebrow: 'Strategic Software Asset Management',
        title: 'VMware cost & sourcing strategy',
        subtitle:
          'Create predictable OpEx and negotiation readiness with a structured asset assessment.',
        cta: { label: 'Request asset management audit', href: '/consultation' },
      },
      problem: {
        title: 'Strategic context',
        items: [
          'Vendor consolidation increases cost volatility.',
          'Core minimums and bundle logic distort actual usage.',
          'Procurement needs a defensible baseline for negotiations.',
          'Sourcing and migration decisions require business-impact scenarios.',
        ],
      },
      solution: {
        title: 'Our approach',
        items: [
          {
            title: 'Asset & cost baseline',
            desc: 'Inventory, usage, and license profiles for negotiations.',
          },
          {
            title: 'Sourcing scenario model',
            desc: 'Renewal, consolidation, and migration scenarios with TCO.',
          },
          {
            title: 'Executive summary',
            desc: 'Board- and procurement-ready recommendations with actions.',
          },
        ],
      },
      steps: {
        title: 'Engagement model',
        items: [
          { title: 'Assess', desc: 'Collect RVTools/CMDB data — no agents.' },
          { title: 'Model', desc: 'Apply cost and scenario models to real usage.' },
          { title: 'Decide', desc: 'Deliver strategy and execution plan with priorities.' },
        ],
      },
      cta: {
        title: 'Ready for a defensible asset strategy?',
        subtitle: 'We deliver the decision baseline for OpEx, sourcing, and migration within weeks.',
        button: { label: 'Request asset management audit', href: '/consultation' },
      },
    },
  }[language];

  return (
    <>
      <SEO title={content.seo.title} description={content.seo.description} />
      <ProductLandingPage content={content} />
    </>
  );
};

export default LicenseDefensePage;
